/**
 * Payment Service
 * Uses HTTP API when enabled, otherwise falls back to localStorage
 */

import axios from 'axios';

const USE_API = process.env.REACT_APP_USE_API !== 'false';
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' }
});

const PAYMENTS_KEY = 'buildzim_payments';
const PAYMENT_COUNTER_KEY = 'buildzim_payment_counter';

// Initialize payment counter
const getPaymentCounter = () => {
  const counter = localStorage.getItem(PAYMENT_COUNTER_KEY);
  return counter ? parseInt(counter) : 1000;
};

const setPaymentCounter = (counter) => {
  localStorage.setItem(PAYMENT_COUNTER_KEY, counter.toString());
};

// Utility functions
const getPayments = () => {
  const payments = localStorage.getItem(PAYMENTS_KEY);
  return payments ? JSON.parse(payments) : [];
};

const savePayments = (payments) => {
  localStorage.setItem(PAYMENTS_KEY, JSON.stringify(payments));
};

const generatePaymentId = () => {
  const counter = getPaymentCounter();
  setPaymentCounter(counter + 1);
  return `PAY-${counter}`;
};

const generateTxHash = () => {
  // Generate mock transaction hash
  const chars = '0123456789abcdef';
  let hash = '0x';
  for (let i = 0; i < 8; i++) {
    hash += chars[Math.floor(Math.random() * chars.length)];
  }
  hash += '...';
  for (let i = 0; i < 4; i++) {
    hash += chars[Math.floor(Math.random() * chars.length)];
  }
  return hash;
};

const generateReference = () => {
  const year = new Date().getFullYear();
  const counter = getPaymentCounter();
  return `INV-${year}-${String(counter).padStart(4, '0')}`;
};

// Simulate network delay
const delay = (ms = 800) => new Promise(resolve => setTimeout(resolve, ms));

// Payment service
const paymentService = {
  // Get all payments for current user
  getPayments: async (userId) => {
    await delay();
    if (USE_API) {
      const { data } = await apiClient.get('/payments', { params: { userId } });
      return Array.isArray(data) ? data : (data.data || []);
    }
    const allPayments = getPayments();
    return allPayments.filter(payment => payment.senderId === userId);
  },

  // Get single payment by ID
  getPayment: async (paymentId) => {
    await delay(300);
    if (USE_API) {
      const { data } = await apiClient.get(`/payments/${paymentId}`);
      return data.data || data;
    }
    const payments = getPayments();
    const payment = payments.find(p => p.id === paymentId);
    
    if (!payment) {
      throw new Error('Payment not found');
    }
    
    return payment;
  },

  // Create new payment
  createPayment: async (userId, paymentData) => {
    await delay(1200); // Longer delay to simulate blockchain transaction
    if (USE_API) {
      const payload = { userId, ...paymentData };
      const { data } = await apiClient.post('/payments', payload);
      return data;
    }
    
    // Validate required fields
    const requiredFields = ['amount', 'recipient', 'phone'];
    for (let field of requiredFields) {
      if (!paymentData[field] || paymentData[field].toString().trim() === '') {
        throw new Error(`${field.replace('_', ' ')} is required.`);
      }
    }

    // Validate amount
    const amount = parseFloat(paymentData.amount);
    if (isNaN(amount) || amount <= 0) {
      throw new Error('Amount must be a valid positive number.');
    }

    if (amount > 100000) {
      throw new Error('Amount exceeds maximum limit of $100,000.');
    }

    // Validate phone number
    const phoneRegex = /^[+]?[\d\s-()]+$/;
    if (!phoneRegex.test(paymentData.phone)) {
      throw new Error('Please enter a valid phone number.');
    }

    const payments = getPayments();
    
    const newPayment = {
      id: generatePaymentId(),
      senderId: userId,
      amount: amount,
      stablecoin: paymentData.stablecoin || 'USDC',
      recipient: {
        name: paymentData.recipient.trim(),
        phone: paymentData.phone.trim(),
        wallet: paymentData.wallet || 'EcoCash',
        verified: Math.random() > 0.3 // Random verification status
      },
      project: {
        id: paymentData.projectId || null,
        title: paymentData.project?.trim() || 'No Project Selected'
      },
      status: 'processing', // Will be updated to 'completed' after delay
      txHash: generateTxHash(),
      reference: paymentData.reference?.trim() || generateReference(),
      date: new Date().toISOString().split('T')[0],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      estimatedCompletion: new Date(Date.now() + 15 * 60 * 1000).toISOString(), // 15 minutes
      fees: {
        networkFee: amount * 0.001, // 0.1% network fee
        serviceFee: amount * 0.005, // 0.5% service fee
        total: amount * 0.006 // Total fees
      },
      exchangeRate: {
        usdToZwl: 25000, // Mock exchange rate
        timestamp: new Date().toISOString()
      }
    };

    payments.push(newPayment);
    savePayments(payments);

    // Simulate blockchain processing - update status after delay
    setTimeout(async () => {
      try {
        await paymentService.updatePaymentStatus(newPayment.id, 'completed');
      } catch (error) {
        console.error('Error updating payment status:', error);
      }
    }, 10000); // Complete after 10 seconds

    return {
      success: true,
      data: newPayment,
      message: 'Smart payment initiated successfully! Processing on blockchain...'
    };
  },

  // Update payment status — mock/localStorage only. Real payment state changes go
  // through the escrow/withdrawal flows; there is no backend /payments/:id/status route.
  updatePaymentStatus: async (paymentId, status, additionalData = {}) => {
    await delay(200);

    const payments = getPayments();
    const paymentIndex = payments.findIndex(p => p.id === paymentId);
    
    if (paymentIndex === -1) {
      throw new Error('Payment not found.');
    }

    const statusHistory = payments[paymentIndex].statusHistory || [];
    statusHistory.push({
      status: payments[paymentIndex].status,
      timestamp: new Date().toISOString()
    });

    payments[paymentIndex] = {
      ...payments[paymentIndex],
      status: status,
      statusHistory: statusHistory,
      updatedAt: new Date().toISOString(),
      ...additionalData
    };

    // Add completion data for completed payments
    if (status === 'completed') {
      payments[paymentIndex].completedAt = new Date().toISOString();
      payments[paymentIndex].confirmations = 12; // Mock confirmations
    }

    savePayments(payments);

    return {
      success: true,
      data: payments[paymentIndex],
      message: `Payment status updated to ${status}!`
    };
  },

  // Cancel payment (only if still processing)
  cancelPayment: async (paymentId) => {
    await delay();
    if (USE_API) {
      const { data } = await apiClient.post(`/payments/${paymentId}/cancel`);
      return data;
    }
    
    const payments = getPayments();
    const paymentIndex = payments.findIndex(p => p.id === paymentId);
    
    if (paymentIndex === -1) {
      throw new Error('Payment not found.');
    }

    if (payments[paymentIndex].status === 'completed') {
      throw new Error('Cannot cancel completed payment.');
    }

    return await paymentService.updatePaymentStatus(paymentId, 'cancelled');
  },

  // Get payment statistics for user
  getPaymentStats: async (userId) => {
    await delay(200);
    if (USE_API) {
      const { data } = await apiClient.get('/payments/stats', { params: { userId } });
      return data.data || data;
    }
    
    const payments = getPayments().filter(p => p.senderId === userId);
    
    const stats = {
      totalPayments: payments.length,
      completedPayments: payments.filter(p => p.status === 'completed').length,
      processingPayments: payments.filter(p => p.status === 'processing').length,
      failedPayments: payments.filter(p => p.status === 'failed').length,
      totalAmount: payments
        .filter(p => p.status === 'completed')
        .reduce((sum, p) => sum + p.amount, 0),
      totalFees: payments
        .filter(p => p.status === 'completed')
        .reduce((sum, p) => sum + (p.fees?.total || 0), 0),
      averageAmount: payments.length > 0 
        ? payments.reduce((sum, p) => sum + p.amount, 0) / payments.length
        : 0,
      monthlyTotal: payments
        .filter(p => {
          const paymentDate = new Date(p.date);
          const now = new Date();
          return paymentDate.getMonth() === now.getMonth() && 
                 paymentDate.getFullYear() === now.getFullYear() &&
                 p.status === 'completed';
        })
        .reduce((sum, p) => sum + p.amount, 0)
    };

    return stats;
  },

  // Search payments
  searchPayments: async (userId, searchTerm, filters = {}) => {
    await delay(300);
    if (USE_API) {
      const { data } = await apiClient.get('/payments/search', { params: { userId, q: searchTerm, ...filters } });
      return Array.isArray(data) ? data : (data.data || []);
    }
    
    let payments = getPayments().filter(p => p.senderId === userId);
    
    // Apply text search
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      payments = payments.filter(p => 
        p.recipient.name.toLowerCase().includes(term) ||
        p.project.title.toLowerCase().includes(term) ||
        p.id.toLowerCase().includes(term) ||
        p.reference.toLowerCase().includes(term)
      );
    }

    // Apply filters
    if (filters.status && filters.status !== 'all') {
      payments = payments.filter(p => p.status === filters.status);
    }

    if (filters.stablecoin) {
      payments = payments.filter(p => p.stablecoin === filters.stablecoin);
    }

    if (filters.wallet) {
      payments = payments.filter(p => p.recipient.wallet === filters.wallet);
    }

    if (filters.minAmount) {
      payments = payments.filter(p => p.amount >= filters.minAmount);
    }

    if (filters.maxAmount) {
      payments = payments.filter(p => p.amount <= filters.maxAmount);
    }

    if (filters.dateFrom) {
      payments = payments.filter(p => p.date >= filters.dateFrom);
    }

    if (filters.dateTo) {
      payments = payments.filter(p => p.date <= filters.dateTo);
    }

    // Sort payments by date (newest first)
    payments.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    return payments;
  },

  // Get payment by transaction hash
  getPaymentByTxHash: async (txHash) => {
    await delay(300);
    if (USE_API) {
      const { data } = await apiClient.get('/payments/by-hash', { params: { txHash } });
      return data.data || data;
    }
    const payments = getPayments();
    const payment = payments.find(p => p.txHash === txHash);
    
    if (!payment) {
      throw new Error('Payment not found for this transaction hash');
    }
    
    return payment;
  },

  // Get supported stablecoins
  getSupportedStablecoins: async () => {
    await delay(100);
    if (USE_API) {
      const { data } = await apiClient.get('/payments/stablecoins');
      return data.data || data;
    }
    return [
      {
        symbol: 'USDC',
        name: 'USD Coin',
        network: 'Polygon',
        fee: 0.001, // 0.1%
        minAmount: 1,
        maxAmount: 100000,
        logo: '/api/placeholder/32/32'
      },
      {
        symbol: 'USDT',
        name: 'Tether USD',
        network: 'Polygon',
        fee: 0.0015, // 0.15%
        minAmount: 1,
        maxAmount: 100000,
        logo: '/api/placeholder/32/32'
      },
      {
        symbol: 'BUSD',
        name: 'Binance USD',
        network: 'BSC',
        fee: 0.0008, // 0.08%
        minAmount: 1,
        maxAmount: 100000,
        logo: '/api/placeholder/32/32'
      },
      {
        symbol: 'DAI',
        name: 'Dai Stablecoin',
        network: 'Polygon',
        fee: 0.0012, // 0.12%
        minAmount: 1,
        maxAmount: 100000,
        logo: '/api/placeholder/32/32'
      }
    ];
  },

  // Get supported mobile wallets
  getSupportedWallets: async () => {
    await delay(100);
    if (USE_API) {
      const { data } = await apiClient.get('/payments/wallets');
      return data.data || data;
    }
    return [
      {
        id: 'ecocash',
        name: 'EcoCash',
        provider: 'Econet Wireless',
        fee: 0.005, // 0.5%
        minAmount: 1,
        maxAmount: 50000,
        logo: '/api/placeholder/32/32'
      },
      {
        id: 'onemoney',
        name: 'OneMoney',
        provider: 'NetOne',
        fee: 0.0045, // 0.45%
        minAmount: 1,
        maxAmount: 50000,
        logo: '/api/placeholder/32/32'
      },
      {
        id: 'mukuru',
        name: 'Mukuru',
        provider: 'Mukuru',
        fee: 0.008, // 0.8%
        minAmount: 10,
        maxAmount: 25000,
        logo: '/api/placeholder/32/32'
      },
      {
        id: 'worldremit',
        name: 'WorldRemit',
        provider: 'WorldRemit',
        fee: 0.007, // 0.7%
        minAmount: 10,
        maxAmount: 30000,
        logo: '/api/placeholder/32/32'
      }
    ];
  }
};

export default paymentService;